import React from 'react';
import { Box, Chip, InputAdornment, Slider, TextField, Typography } from '@mui/material';
import CustomModal from '../../shared/CustomModal/CustomModal';
import CustomCard from '../../shared/CustomCard/CustomCard';
import CustomButton from '../../shared/CustomButton/CustomButton';
import CustomAutocomplete from '../../shared/CustomAutocomplete/CustomAutocomplete';
import zeplinColor from '../../../theme/Color';
import { DealsService } from '../../../service/deals.service';
import { TeamService } from '../../../service/team.service';
import { initialAlertState, useAlertStatus } from '../../../customHook/useAlertStatus';
import CustomAlert from '../../shared/CustomAlert/CustomAlert';

type ClientOption = { Name: string; Id: string };
type AssignmentPrefill = {
  id: string;
  clientId: string;
  clientName: string;
  capacityWeight: number;
};

const CLIENT_SEARCH_DEBOUNCE_MS = 350;

interface AddAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  currentCapacity?: number;
  memberId?: string;
  assignment?: AssignmentPrefill | null;
  onSaved?: () => void;
}

const getCapacityStatus = (total: number) => {
  if (total <= 60) return { label: 'Healthy', color: zeplinColor.Success };
  if (total <= 100) return { label: 'High', color: zeplinColor.AlertInfo };
  return { label: 'Over Capacity', color: zeplinColor.Error };
};

const AddAssignmentModal: React.FC<AddAssignmentModalProps> = ({
  open,
  onClose,
  currentCapacity = 0,
  memberId,
  assignment = null,
  onSaved,
}) => {
  const [selectedClient, setSelectedClient] = React.useState<ClientOption | null>(null);
  const [clientSearch, setClientSearch] = React.useState('');
  const [clientOptions, setClientOptions] = React.useState<ClientOption[]>([]);
  const [clientLoading, setClientLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [capacityWeight, setCapacityWeight] = React.useState(0);
  const [inputValue, setInputValue] = React.useState('0');
  const isEditMode = Boolean(assignment?.id);
  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();

  const fetchClients = React.useCallback(async (searchText?: string) => {
    try {
      setClientLoading(true);
      const data = await DealsService.getAllClients(searchText);
      const clients = data?.clients || (Array.isArray(data) ? data : []);
      const formattedClients = clients
        .map((item: any) => ({
          Name: item.Name || item.name || item.clientName || '',
          Id: item.AccountId || item.Id || item.id || item.clientId || '',
        }))
        .filter((item: any) => item.Name && item.Id);
      setClientOptions(formattedClients);
    } catch {
      setClientOptions([]);
    } finally {
      setClientLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;

    if (assignment) {
      setCapacityWeight(assignment.capacityWeight || 0);
      setInputValue(String(assignment.capacityWeight || 0));
      setSelectedClient(
        assignment.clientId
          ? {
              Name: assignment.clientName || '',
              Id: assignment.clientId,
            }
          : null,
      );
    } else {
      setCapacityWeight(0);
      setInputValue('0');
      setSelectedClient(null);
    }
    setClientSearch('');
    fetchClients();
  }, [open, assignment, fetchClients]);

  React.useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      fetchClients(clientSearch.trim() || undefined);
    }, CLIENT_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [clientSearch, fetchClients, open]);

  React.useEffect(() => {
    if (!selectedClient?.Id) return;

    setClientOptions((prev) => {
      if (prev.some((item) => item.Id === selectedClient.Id)) return prev;
      return [selectedClient, ...prev];
    });
  }, [selectedClient]);

  const newTotal = currentCapacity + capacityWeight;
  const capacityStatus = getCapacityStatus(newTotal);

  const handleSliderChange = (_: Event, value: number | number[]) => {
    const v = value as number;
    setCapacityWeight(v);
    setInputValue(String(v));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInputValue(raw);
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
      setCapacityWeight(parsed);
    }
  };

  const handleInputBlur = () => {
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed) || parsed < 0) {
      setCapacityWeight(0);
      setInputValue('0');
    } else if (parsed > 100) {
      setCapacityWeight(100);
      setInputValue('100');
    }
  };

  const handleConfirm = async () => {
    if (!memberId || !selectedClient?.Id) return;
    const operationType = isEditMode ? 'update' : 'create';

    const payload = {
      memberId,
      clientId: selectedClient.Id,
      capacityWeight,
    };

    setSubmitting(true);
    try {
      startOperation(
        operationType,
        isEditMode ? 'Updating assignment...' : 'Creating assignment...',
      );
      if (isEditMode && assignment?.id) {
        await TeamService.updateAssignment(assignment.id, payload);
      } else {
        await TeamService.createAssignment(payload);
      }
      completeOperation(
        operationType,
        isEditMode ? 'Assignment updated successfully.' : 'Assignment created successfully.',
      );
      onSaved && onSaved();
      onClose();
    } catch (error: any) {
      completeOperation(
        operationType,
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Failed to save assignment.',
        true,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />

      <CustomModal handleClose={onClose} open={open}>
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: { xs: '20px', xl: '24px' },
              backgroundColor: zeplinColor.Neutral1,
              width: '484px',
              boxShadow: '0px 8px 32px rgba(0,0,0,0.12)',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
                  {isEditMode ? 'Edit Assignment' : 'Add New Assignment'}
                </Typography>
                <img
                  src='/assets/cross.svg'
                  alt='close'
                  style={{ cursor: 'pointer', width: 20, height: 20 }}
                  onClick={onClose}
                />
              </Box>

              {/* Client searchable dropdown */}
              <CustomAutocomplete
                popupIcon='/assets/Search.svg'
                options={clientOptions}
                label='Client'
                getOptionLabel={(option: any) => option?.Name || ''}
                handleDeleteItems={() => setSelectedClient(null)}
                selectedItems={{}}
                handleSelection={(option: any) => setSelectedClient(option)}
                value={selectedClient}
                multiple={false}
                handleFilter={(_: React.SyntheticEvent, value: string) => setClientSearch(value)}
                placeholder='Search client...'
                required
                currentLength={clientOptions.length}
                totalLength={clientOptions.length}
                next={() => {}}
                loading={clientLoading}
              />

              {/* Capacity Weight */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                  variant='responsiveBody3'
                  color={zeplinColor.BasicBlack}
                  fontWeight={600}
                >
                  Capacity Weight
                </Typography>
                <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                  Set the bandwidth allocation for this assignment.
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {/* Slider + marks labels stacked as flex column, gap 12 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0px', width: 342 }}>
                    {/* Slider with static split background */}
                    <Box sx={{ position: 'relative', width: 342 }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '45%',
                          transform: 'translateY(-50%)',
                          left: 0,
                          width: '100%',
                          height: 8,
                          borderRadius: '20px',
                          background: `linear-gradient(to right, ${zeplinColor.Success} 0%, ${zeplinColor.Success} 60%, ${zeplinColor.AlertInfo} 60%, ${zeplinColor.AlertInfo} 100%)`,
                          pointerEvents: 'none',
                        }}
                      />
                      <Slider
                        value={capacityWeight}
                        min={0}
                        max={100}
                        step={1}
                        onChange={handleSliderChange}
                        sx={{
                          width: 342,
                          height: 8,
                          padding: '13px 0',
                          color: 'transparent',
                          '& .MuiSlider-thumb': {
                            width: 16,
                            height: 16,
                            backgroundColor: zeplinColor.Neutral1,
                            boxShadow: '0px 0px 5px 0px #00000029',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: zeplinColor.BasicBlack,
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                            },
                            '&:hover, &.Mui-focusVisible': {
                              boxShadow: '0px 0px 5px 0px #00000029',
                            },
                            '&:before': {
                              display: 'none',
                            },
                          },
                          '& .MuiSlider-track': { display: 'none' },
                          '& .MuiSlider-rail': { display: 'none' },
                        }}
                      />
                    </Box>

                    {/* Marks row */}
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <Typography
                        variant='label1'
                        color={zeplinColor.Neutral8}
                        sx={{ position: 'absolute', left: 0 }}
                      >
                        0%
                      </Typography>
                      <Typography
                        variant='label1'
                        color={zeplinColor.Neutral8}
                        sx={{ position: 'absolute', left: '60%', transform: 'translateX(-50%)' }}
                      >
                        60%
                      </Typography>
                      <Typography
                        variant='label1'
                        color={zeplinColor.Neutral8}
                        sx={{ position: 'absolute', left: '100%', transform: 'translateX(-50%)' }}
                      >
                        100%
                      </Typography>
                    </Box>
                  </Box>
                  <TextField
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    variant='outlined'
                    size='small'
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                            %
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ min: 0, max: 100, style: { textAlign: 'right', width: 40 } }}
                    sx={{
                      width: '80px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '& fieldset': { borderColor: zeplinColor.Neutral5 },
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Expected Capacity Impact card */}
              <Box
                sx={{
                  width: '436px',
                  minHeight: '115px',
                  borderRadius: '14px',
                  border: `1px solid ${zeplinColor.Neutral5}`,
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <Typography
                  variant='responsiveHeading4'
                  color={zeplinColor.BasicBlack}
                  fontWeight={600}
                >
                  Expected Capacity Impact
                </Typography>
                <Box sx={{ display: 'flex', gap: '24px' }}>
                  {/* Current Capacity */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                      {currentCapacity}%
                    </Typography>
                    <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                      Current Capacity
                    </Typography>
                  </Box>

                  {/* New Total */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Typography
                      variant='responsiveBody3'
                      color={newTotal > 100 ? zeplinColor.Error : zeplinColor.BasicBlack}
                    >
                      {newTotal}%
                    </Typography>
                    <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                      New Total
                    </Typography>
                  </Box>

                  {/* Capacity Status */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Typography variant='responsiveBody3' color={capacityStatus.color}>
                        {capacityStatus.label}
                      </Typography>
                      {newTotal > 100 && (
                        <Chip
                          label='Over Capacity'
                          size='small'
                          sx={{
                            backgroundColor: zeplinColor.Error,
                            color: zeplinColor.Neutral1,
                            height: '20px',
                            '& .MuiChip-label': { px: '8px', fontSize: 11 },
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                      Capacity Status
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <CustomButton
                  disabled={submitting}
                  onClick={onClose}
                  variant='outlined'
                  color='primary'
                  style={{
                    borderRadius: '8px',
                    height: '40px',
                    padding: '8px 16px',
                    flex: 1,
                    border: `1px solid ${zeplinColor.BasicBlack}`,
                    '&:hover': { backgroundColor: zeplinColor.Neutral3 },
                  }}
                  textStyle={{ color: zeplinColor.BasicBlack }}
                  text='Cancel'
                />
                <CustomButton
                  disabled={!selectedClient || submitting}
                  onClick={handleConfirm}
                  variant='contained'
                  color='primary'
                  style={{
                    borderRadius: '8px',
                    height: '40px',
                    padding: '8px 16px',
                    flex: 1,
                    backgroundColor:
                      !selectedClient || submitting ? zeplinColor.Neutral5 : zeplinColor.Brand1,
                  }}
                  textStyle={{ color: zeplinColor.Neutral1 }}
                  text={submitting ? 'Saving...' : 'Confirm Assignment'}
                />
              </Box>
            </Box>
          </CustomCard>
        </Box>
      </CustomModal>
    </>
  );
};

export default AddAssignmentModal;
